import Section from 'app/frontend/base/components/Section'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Navigate, useParams } from 'react-router-dom'
import useReferenceStore from 'app/frontend/base/state/useReferenceStore'
import EventHeader from './components/EventHeader'
import {
  useEventActivitiesPlanningInfo,
  useTeamEvent,
} from 'app/frontend/base/config/queries'
import EventActivityBlock from './components/EventActivityBlock'
import { useMemo, useState } from 'react'
import AddParticipantModal from './components/AddParticipantModal'
import AddEventActivitiesDialog from './components/AddEventActivitiesDialog'
import useApplyEventUpdate from './hooks/useApplyEventUpdate'
import useOptimisticEventUpdate from './hooks/useOptimisticEventUpdate'
import { AddEventFormValues } from '../TeamEventOverview/components/AddEventDialog'
import { TeamEventUpdateProvider } from './context/TeamEventUpdateContext'
import EventActivityGrid from './components/EventActivityGrid'
import { cn } from '@/lib/utils'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import {
  TeamEventParticipantsProvider,
  useTeamEventParticipantsContext,
} from './context/TeamEventParticipantsContext'
import {
  TeamEventPlanningInfoProvider,
  useTeamEventPlanningInfoState,
} from './context/TeamEventPlanningInfoContext'
import { Switch } from '@/components/ui/switch'
import Card from 'app/frontend/base/components/Card'
import EventAttendanceSummary from './components/EventAttendanceSummary'
import useTeamEventMutations from './mutations/useTeamEventMutations'
import ViewerSignupCard from './components/ViewerSignupCard'
import {
  CreateEventActivitiesPayload,
  UpdateEventActivityPayload,
} from 'app/frontend/base/api/event'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import ImportFromPreviousEventDialog from './components/ImportFromPreviousEventDialog'
import { Settings2Icon } from 'lucide-react'
import useEventActivityLinkedPlanData, {
  EventActivityLinkedPlanData,
} from './hooks/useEventActivityLinkedPlanData'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'
import { buildEventActivityLinkedPlanNote } from './components/eventActivityNoteExport'

type TeamEventLinkedPlanDataProviderProps = {
  activities: wowaudit.EventActivity[]
  activityType: wowaudit.Event['activityType']
  children: (data: {
    linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
    isRaidPlansLoading: boolean
  }) => React.ReactNode
}

const TeamEventLinkedPlanDataProvider = ({
  activities,
  activityType,
  children,
}: TeamEventLinkedPlanDataProviderProps) => {
  const { linkedPlanDataByActivityId, isRaidPlansLoading } =
    useEventActivityLinkedPlanData({
      activities,
      enabled: activityType === 'raids',
    })
  return children({ linkedPlanDataByActivityId, isRaidPlansLoading })
}

type CopyAllNotesButtonProps = {
  activities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
}

const CopyAllNotesButton = ({
  activities,
  linkedPlanDataByActivityId,
}: CopyAllNotesButtonProps) => {
  const { allEntries } = useTeamEventParticipantsContext()
  const { copied, copyToClipboard } = useCopyToClipboard()
  const copiedAllNotes = copied !== undefined

  const handleCopyAllNotes = () => {
    const allNotes = activities
      .filter((activity) => activity.active)
      .map((activity) =>
        buildEventActivityLinkedPlanNote({
          activity,
          linkedPlanData: linkedPlanDataByActivityId.get(activity.id),
          allEntries,
        }),
      )
      .filter((note) => note.length > 0)
      .join('\n\n')
    if (!allNotes) return
    copyToClipboard(allNotes)
  }

  return (
    <Button size='sm' onClick={handleCopyAllNotes}>
      {copiedAllNotes ? 'Copied all notes!' : 'Copy all notes'}
    </Button>
  )
}

const TeamEvent = () => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()
  const { team, guild } = useTeam()
  const { eventId } = useParams()
  const { getReference } = useReferenceStore()
  const [addParticipantsOpen, setAddParticipantsOpen] = useState(false)
  const [addActivitiesOpen, setAddActivitiesOpen] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isResetSelectionsConfirmOpen, setIsResetSelectionsConfirmOpen] =
    useState(false)
  const canAdminEdit = accessLevel === 'admin'
  const parsedEventId = Number(eventId)
  const numericEventId = Number.isFinite(parsedEventId)
    ? parsedEventId
    : undefined
  const {
    data: event,
    isLoading: isLoadingEvent,
    isError: hasEventError,
  } = useTeamEvent(team.id, numericEventId)

  const { showPlanningInfo, setShowPlanningInfo } =
    useTeamEventPlanningInfoState(team.id, event?.activityType ?? 'custom')

  const { data: eventActivitiesPlanningInfo } = useEventActivitiesPlanningInfo(
    team.id,
    numericEventId,
    event?.activities || [],
    canAdminEdit || showPlanningInfo,
  )
  const applyEventUpdate = useApplyEventUpdate()
  const optimisticEventUpdate = useOptimisticEventUpdate(numericEventId)
  const {
    updateEventMutation,
    updateStatusMutation,
    updateSelectionMutation,
    removeGuestMutation,
    updateActivitySelectionForAllParticipantsMutation,
    updateEventActivityMutation,
    updateEventActivityOrderMutation,
    copyEventActivitySelectionsMutation,
    exportEventActivityOverridesMutation,
    createEventActivitiesMutation,
    deleteEventActivityMutation,
    importEventFromPreviousEventMutation,
    resetEventSelectionsMutation,
  } = useTeamEventMutations({
    teamId: team.id,
    eventId: numericEventId,
    applyEventUpdate,
    optimisticEventUpdate,
    onError: (error) => {
      displayErrorToast(error as Error, t)
    },
  })

  const updateContextValue = {
    isSaving:
      updateEventMutation.isPending ||
      updateStatusMutation.isPending ||
      updateSelectionMutation.isPending ||
      removeGuestMutation.isPending ||
      updateActivitySelectionForAllParticipantsMutation.isPending ||
      updateEventActivityMutation.isPending ||
      updateEventActivityOrderMutation.isPending ||
      copyEventActivitySelectionsMutation.isPending ||
      exportEventActivityOverridesMutation.isPending ||
      createEventActivitiesMutation.isPending ||
      deleteEventActivityMutation.isPending ||
      importEventFromPreviousEventMutation.isPending ||
      resetEventSelectionsMutation.isPending,
    editEvent: async (values: AddEventFormValues) => {
      await updateEventMutation.mutateAsync(values)
    },
    updateEvent: async (values: Partial<wowaudit.Event>) => {
      await updateEventMutation.mutateAsync(values)
    },
    updateStatus: (
      eventParticipantId: number,
      status: wowaudit.EventParticipantReference['status'],
    ) => {
      updateStatusMutation.mutate({
        eventParticipantId,
        status,
      })
    },
    updateSelection: (
      eventParticipantId: number,
      updates: {
        role?: string
        specId?: number | null
        characterId?: number
        selected?: boolean
      },
      activityId?: number,
    ) => {
      updateSelectionMutation.mutate({
        eventParticipantId,
        activityId,
        ...updates,
      })
    },
    updateActivitySelectionForAllParticipants: (
      activityId: number,
      selected: boolean,
    ) => {
      updateActivitySelectionForAllParticipantsMutation.mutate({
        activityId,
        selected,
      })
    },
    updateEventActivity: (
      activityId: number,
      updates: UpdateEventActivityPayload,
    ) => {
      return updateEventActivityMutation
        .mutateAsync({
          activityId,
          eventActivity: updates,
        })
        .then(() => undefined)
    },
    updateEventActivityOrder: (activityOrder: number[]) => {
      return updateEventActivityOrderMutation
        .mutateAsync({ activityOrder })
        .then(() => undefined)
    },
    copySelectionsFromActivity: (
      activityId: number,
      sourceActivityId: number,
      sourceEventId?: number,
    ) => {
      return copyEventActivitySelectionsMutation
        .mutateAsync({ activityId, sourceActivityId, sourceEventId })
        .then(() => undefined)
    },
    exportEventActivityOverrides: (activityId: number) => {
      return exportEventActivityOverridesMutation
        .mutateAsync({ activityId })
        .then(() => undefined)
    },
    addEventActivities: (eventActivity: CreateEventActivitiesPayload) => {
      return createEventActivitiesMutation
        .mutateAsync({ eventActivity })
        .then(() => undefined)
    },
    removeEventActivity: (activityId: number) => {
      return deleteEventActivityMutation
        .mutateAsync({ activityId })
        .then(() => undefined)
    },
    importEventFromPreviousEvent: (sourceEventId: number) => {
      return importEventFromPreviousEventMutation
        .mutateAsync({ sourceEventId })
        .then(() => undefined)
    },
    resetAllSelections: () => {
      return resetEventSelectionsMutation.mutateAsync().then(() => undefined)
    },
    removeGuest: (eventParticipantId: number) => {
      removeGuestMutation.mutate(eventParticipantId)
    },
  }
  const sortedActivities = useMemo(() => {
    if (!event?.activities?.length) return []

    return event.activities
      .slice()
      .sort((left, right) => left.order - right.order)
  }, [event?.activities])
  const initialEventReference = getReference<wowaudit.EventReference>(
    eventId ? `${team.path}/events/${eventId}` : undefined,
  )

  if (!numericEventId) {
    return <Navigate to={`${team.path}/events`} replace />
  }

  if (!isLoadingEvent && (hasEventError || !event)) {
    return <Navigate to={`${team.path}/events`} replace />
  }

  return (
    <Section
      title={`Event | ${team.name} | ${guild.name}`}
      className={cn(
        'opacity-0 transition-opacity',
        !isLoadingEvent && 'opacity-100',
      )}
    >
      <TeamEventUpdateProvider value={updateContextValue}>
        <TeamEventPlanningInfoProvider
          value={{ showPlanningInfo, setShowPlanningInfo }}
        >
          <EventHeader
            eventId={numericEventId}
            initialEventReference={initialEventReference}
            onAddParticipants={() => setAddParticipantsOpen(true)}
            onAddActivities={() => setAddActivitiesOpen(true)}
          />

          {event ? (
            <TeamEventParticipantsProvider
              event={event}
              activities={sortedActivities}
              planningInfo={eventActivitiesPlanningInfo}
            >
              <TeamEventLinkedPlanDataProvider
                activities={sortedActivities}
                activityType={event.activityType}
              >
                {({ linkedPlanDataByActivityId, isRaidPlansLoading }) => (
                  <>
                    <ViewerSignupCard
                      event={event}
                      activities={sortedActivities}
                      linkedPlanDataByActivityId={linkedPlanDataByActivityId}
                    />

                    <div className='mt-6 mb-2 flex justify-between'>
                      {event ? <EventAttendanceSummary event={event} /> : null}

                      <div className='flex items-center gap-2'>
                        <CopyAllNotesButton
                          activities={sortedActivities}
                          linkedPlanDataByActivityId={
                            linkedPlanDataByActivityId
                          }
                        />

                        {event.activityType === 'raids' && canAdminEdit ? (
                          <>
                            <Card className='flex items-center gap-2 h-9 px-2'>
                              <Switch
                                id='show-planning-info'
                                checked={showPlanningInfo}
                                onCheckedChange={(checked) =>
                                  setShowPlanningInfo(checked === true)
                                }
                              />
                              <label
                                htmlFor='show-planning-info'
                                className='cursor-pointer select-none'
                              >
                                Planning info
                              </label>
                            </Card>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant='outline' size='sm'>
                                  <Settings2Icon className='h-4 w-4' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end'>
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setIsImportDialogOpen(true)
                                  }}
                                >
                                  Import from previous event
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setIsResetSelectionsConfirmOpen(true)
                                  }}
                                >
                                  Reset all selections
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        ) : null}
                      </div>
                    </div>

                    {event.activityType === 'raids' ? (
                      <EventActivityGrid
                        event={event}
                        activities={sortedActivities}
                        linkedPlanDataByActivityId={linkedPlanDataByActivityId}
                        isRaidPlansLoading={isRaidPlansLoading}
                      />
                    ) : (
                      <EventActivityBlock activity={sortedActivities[0]} />
                    )}
                  </>
                )}
              </TeamEventLinkedPlanDataProvider>

              <AddParticipantModal
                open={addParticipantsOpen}
                setOpen={setAddParticipantsOpen}
                event={event}
              />
              <AddEventActivitiesDialog
                open={addActivitiesOpen}
                setOpen={setAddActivitiesOpen}
                event={event}
                isPending={createEventActivitiesMutation.isPending}
                onSubmit={async (payload) => {
                  await updateContextValue.addEventActivities(payload)
                }}
              />
              {canAdminEdit ? (
                <>
                  <ImportFromPreviousEventDialog
                    event={event}
                    open={isImportDialogOpen}
                    onOpenChange={setIsImportDialogOpen}
                    onImport={async (sourceEventId) => {
                      await updateContextValue.importEventFromPreviousEvent(
                        sourceEventId,
                      )
                    }}
                  />
                  <ConfirmDialog
                    open={isResetSelectionsConfirmOpen}
                    title='Reset all selections?'
                    description='This will clear all activity selections for all participants in this event.'
                    variant='destructive'
                    onCancel={() => setIsResetSelectionsConfirmOpen(false)}
                    onContinue={async () => {
                      await updateContextValue.resetAllSelections()
                      setIsResetSelectionsConfirmOpen(false)
                    }}
                  />
                </>
              ) : null}
            </TeamEventParticipantsProvider>
          ) : null}
        </TeamEventPlanningInfoProvider>
      </TeamEventUpdateProvider>
    </Section>
  )
}

export default TeamEvent
